import React, { useEffect } from 'react';
import { Grid, TextField, MenuItem, Autocomplete } from '@mui/material';
import CANADIAN_INSTITUTIONS from '../../data/canadianInstitutions';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import Divider from '@mui/material/Divider';
import { isStringEmpty } from '../../utils/formValidation';
import StartEndDatePicker from './StartEndDatePicker';
import { textFieldHandlerBuilder } from '../../utils/inputHandlers';
import NotificationBar from '../common/NotificationBar';
import ProfileSectionController from './ProfileSectionController';
import APICaller, { MethodType } from '../common/APICaller';

export interface Education {
  id: number | null;
  schoolName: string;
  degree: string;
  gpa: string;
  startDate: string | null;
  endDate: string | null;
  enrollmentStatus?: string | null;
}

interface EducationRecordProp {
  data: Education;
  deleteRecord: (idx: number) => void;
  idx: number;
  viewMode?: boolean;
}

const EducationRecord: React.FC<EducationRecordProp> = ({
  data,
  deleteRecord,
  idx,
  viewMode,
}) => {
  const [school, setSchool] = useState<string>(data.schoolName || '');
  const [customSchool, setCustomSchool] = useState<string>(
    data.schoolName && !CANADIAN_INSTITUTIONS.includes(data.schoolName) && data.schoolName !== ''
      ? data.schoolName
      : '',
  );
  const isOtherSelected = school === 'Other';
  const [gpa, setGPA] = useState<string>(data.gpa || '');
  const [degree, setDegree] = useState<string>(data.degree || '');
  const [enrollmentStatus, setEnrollmentStatus] = useState<string>(data.enrollmentStatus || '');
  const [startDate, setStartDate] = useState<Dayjs | null>(
    data.startDate ? dayjs(data.startDate) : null,
  );
  const [startDateValid, setStartDateValid] = useState<boolean>(true);
  const [endDateValid, setEndDateValid] = useState<boolean>(true);
  const [endDate, setEndDate] = useState<Dayjs | null>(
    data.endDate ? dayjs(data.endDate) : null,
  );

  const [isSchoolEmpty, setIsSchoolEmpty] = useState<boolean>(false);
  const [isGPAEmpty, setIsGPAEmpty] = useState<boolean>(false);
  const [isDegreeEmpty, setIsDegreeEmpty] = useState<boolean>(false);

  const [showNotification, setShowNotification] = useState(false);
  const [educationId, setEducationId] = useState<number | null>(data.id);

  useEffect(() => {
    if (CANADIAN_INSTITUTIONS.includes(data.schoolName)) {
      setSchool(data.schoolName);
      setCustomSchool('');
    } else if (data.schoolName) {
      setSchool('Other');
      setCustomSchool(data.schoolName);
    } else {
      setSchool('');
      setCustomSchool('');
    }
    setGPA(data.gpa);
    setDegree(data.degree);
    setEnrollmentStatus(data.enrollmentStatus || '');
    setStartDate(data.startDate ? dayjs(data.startDate) : null);
    setEndDate(data.endDate ? dayjs(data.endDate) : null);
    setEducationId(data.id);
  }, [data]);

  const saveButtonHandler = () => {
    const resolvedSchoolName = school === 'Other' ? customSchool : school;
    const schoolFieldEmpty = isStringEmpty(resolvedSchoolName);
    const gpaFieldEmpty = isStringEmpty(gpa);
    const degreeFieldEmpty = isStringEmpty(degree);
    setIsSchoolEmpty(schoolFieldEmpty);
    setIsGPAEmpty(gpaFieldEmpty);
    setIsDegreeEmpty(degreeFieldEmpty);
    setStartDateValid(startDate != null);
    setEndDateValid(endDate != null);
    if (
      !schoolFieldEmpty &&
      !gpaFieldEmpty &&
      !degreeFieldEmpty &&
      !(startDate == null) &&
      !(endDate == null)
    ) {
      const route = educationId
        ? `/profile/education/${educationId}/update`
        : '/profile/education/create';
      const method = educationId ? MethodType.PUT : MethodType.POST;
      const resolvedSchool = isOtherSelected ? customSchool : school;
      const data = {
        education: {
          schoolName: resolvedSchool,
          degree,
          gpa: parseFloat(gpa),
          startDate: startDate?.toString(),
          endDate: endDate?.toString(),
          enrollmentStatus: enrollmentStatus || null,
        },
      };
      APICaller(route, method, data).then(res => {
        if (res.errorMessage) {
          alert(res.errorMessage);
          return;
        }
        if (res.educationId) {
          setShowNotification(true);
          setEducationId(res.educationId);
        }
      });
    }
  };
  const deleteButtonHandler = () => {
    if (educationId) {
      APICaller(
        `/profile/education/${educationId}/delete`,
        MethodType.DELETE,
      ).then(res => {
        if (!res.errorMessage) {
          deleteRecord(idx);
          console.log('education record delete');
        } else {
          alert(res.errorMessage);
        }
      });
    } else {
      deleteRecord(idx);
    }
  };

  return (
    <>
      <Grid
        container
        direction="row"
        alignItems="center"
        sx={{ marginBottom: '10px' }}
      >
        <Divider
          sx={{
            marginTop: idx === 0 ? '6px' : '3vh',
            width: '100%',
            fontFamily: 'inherit',
            fontWeight: 'bold',
          }}
          textAlign="center"
        >
          {`Education ${idx + 1}`}
        </Divider>
        <Grid container item xs={12} md={12} spacing={2}>
          <Grid item xs={12} sm={8} md={8}>
            <Autocomplete
              options={CANADIAN_INSTITUTIONS}
              value={school || null}
              onChange={(_event, newValue) => {
                setSchool(newValue || '');
                if (newValue !== 'Other') {
                  setCustomSchool('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={isSchoolEmpty}
                  required
                  label="School"
                  name="school"
                  id="school"
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' },
                  }}
                />
              )}
              filterOptions={(options, { inputValue }) => {
                const filtered = options.filter((option) =>
                  option.toLowerCase().includes(inputValue.toLowerCase()),
                );
                return filtered;
              }}
              ListboxProps={{
                style: { maxHeight: 250 },
              }}
              noOptionsText="No matching institution found"
              autoHighlight
              openOnFocus
              blurOnSelect
            />
          </Grid>
          {isOtherSelected && (
            <Grid item xs={12} sm={8} md={8}>
              <TextField
                error={isSchoolEmpty}
                required
                fullWidth
                name="custom_school"
                label="Enter your school name"
                type="text"
                id="custom_school"
                value={customSchool}
                onChange={(e) => setCustomSchool(e.target.value)}
                InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={4} md={4}>
            <TextField
              error={isGPAEmpty}
              required
              fullWidth
              name="gpa"
              label="GPA"
              type="text"
              id="gpa"
              value={gpa}
              onChange={textFieldHandlerBuilder(setGPA)}
              InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              error={isDegreeEmpty}
              required
              fullWidth
              name="degree"
              label="Degree"
              type="text"
              id="degree"
              value={degree}
              onChange={textFieldHandlerBuilder(setDegree)}
              InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              select
              fullWidth
              name="enrollment_status"
              label="Are you currently enrolled in studies?"
              id="enrollment_status"
              value={enrollmentStatus}
              onChange={e => setEnrollmentStatus(e.target.value)}
              InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: { maxHeight: 48 * 4.5 + 8, width: 320 },
                  },
                },
              }}
            >
              <MenuItem value={"Yes – Full-time"}>Yes – Full-time</MenuItem>
              <MenuItem value={"Yes – Part-time"}>Yes – Part-time</MenuItem>
              <MenuItem value={"No"}>No</MenuItem>
            </TextField>
          </Grid>
          <StartEndDatePicker
            startDateValue={startDate}
            startDateValid={startDateValid}
            endDateValue={endDate}
            endDateValid={endDateValid}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            dateRequired={true}
            endLabel="End Date (or expected)"
          />
        </Grid>
        {viewMode ? null : (
          <ProfileSectionController
            saveButtonHandler={saveButtonHandler}
            deleteButtonHandler={deleteButtonHandler}
          />
        )}
      </Grid>
      {viewMode ? null : (
        <NotificationBar
          open={showNotification}
          setOpen={setShowNotification}
          title="Successs!"
          message="Your change has been saved!"
        />
      )}
    </>
  );
};

export default EducationRecord;
